<script lang="ts" setup>
import { ref, computed } from "vue";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import OrganizationSwitchModal from "@/views/shared/components/modals/OrganizationSwitchModal.vue";
import DataView from "@/components/DataView.vue";
import {
  usePartnerActivities,
  renderActivityTitle,
  formatActivityTimestamp,
  activityIconClass,
  activityColorClass,
  activitySeverityBadgeClass,
  ACTIVITY_CATEGORY_OPTIONS,
  ACTIVITY_SEVERITY_OPTIONS,
} from "@/queries/activities";
import type {
  PartnerActivityRow,
  ActivityCategory,
  ActivitySeverity,
} from "@/types";
import { useActivityOrgSwitch } from "@/composables/useActivityOrgSwitch";
import type { Config } from "datatables.net";

// =====================================================================
// Filter state (drives the live server query reactively)
// =====================================================================
const searchQuery = ref("");
const selectedClient = ref<string>("");
const selectedCategory = ref<ActivityCategory | "">("");
const selectedSeverity = ref<ActivitySeverity | "">("");
const dateFrom = ref("");
const dateTo = ref("");

// Server-side params — categories/severities/clientOrgId/before are sent to the API. The
// search box and date-from filter narrow the response client-side (search is full-text against
// the rendered title, and `dateFrom` is a lower bound — server supports `before` upper bound only).
const serverCategories = computed<ActivityCategory[] | undefined>(() =>
  selectedCategory.value ? [selectedCategory.value] : undefined,
);
const serverSeverities = computed<ActivitySeverity[] | undefined>(() =>
  selectedSeverity.value ? [selectedSeverity.value] : undefined,
);
const serverClientOrgId = computed(() => selectedClient.value || undefined);

const { activities, activitiesLoading: isLoading } = usePartnerActivities({
  clientOrgId: serverClientOrgId,
  categories: serverCategories,
  severities: serverSeverities,
  take: 100,
});

// =====================================================================
// Client-side narrowing — full-text search + dateFrom lower bound + dateTo upper bound.
// The server already applies clientOrgId/category/severity, so we don't re-apply them here.
// =====================================================================
const filteredActivities = computed(() => {
  let rows = activities.value;

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    rows = rows.filter((a) => {
      const title = renderActivityTitle(a).toLowerCase();
      const client = a.clientOrgName.toLowerCase();
      return title.includes(q) || client.includes(q);
    });
  }

  if (dateFrom.value) {
    const fromMs = new Date(dateFrom.value).getTime();
    rows = rows.filter((a) => new Date(a.occurredOn).getTime() >= fromMs);
  }

  if (dateTo.value) {
    const toMs = new Date(dateTo.value).getTime();
    rows = rows.filter((a) => new Date(a.occurredOn).getTime() <= toMs);
  }

  return rows;
});

// Build the client dropdown from the data — only clients that have at least one activity show up.
const uniqueClients = computed(() => {
  const seen = new Map<string, string>();
  for (const a of activities.value) {
    if (!seen.has(a.clientOrgId)) seen.set(a.clientOrgId, a.clientOrgName);
  }
  return Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

function clearFilters() {
  searchQuery.value = "";
  selectedClient.value = "";
  selectedCategory.value = "";
  selectedSeverity.value = "";
  dateFrom.value = "";
  dateTo.value = "";
}

function formatDateTime(timestamp: string) {
  return new Date(timestamp).toLocaleString();
}

// =====================================================================
// Org-switch modal — clicking an activity prompts a switch to that client's org.
// =====================================================================
const orgSwitch = useActivityOrgSwitch();

// =====================================================================
// DataTables column definitions
// =====================================================================
const tableOptions = {
  columns: [
    { data: "id", title: "ID", visible: false },
    {
      data: "occurredOn",
      title: "Date",
      render: (data: string) =>
        `<small class="text-muted">${formatDateTime(data)}</small>`,
    },
    {
      data: null,
      title: "Activity",
      render: (_data: unknown, _type: string, row: PartnerActivityRow) => {
        const icon = activityIconClass(row);
        const color = activityColorClass(row.severity);
        const title = renderActivityTitle(row);
        return `<div class="d-flex align-items-start gap-2">
          <i class="${icon} ${color} fs-5 mt-1"></i>
          <div>
            <div class="fw-medium">${title}</div>
            <small class="text-muted">${row.category}</small>
          </div>
        </div>`;
      },
    },
    {
      data: "clientOrgName",
      title: "Client",
      render: (data: string) =>
        `<span class="badge bg-secondary">${data}</span>`,
    },
    {
      data: "severity",
      title: "Status",
      render: (data: ActivitySeverity) =>
        `<span class="badge ${activitySeverityBadgeClass(data)}">${data}</span>`,
    },
    {
      data: null,
      title: "Actions",
      orderable: false,
      render: () =>
        `<button class="bb-btn-secondary-cta btn-sm text-nowrap"><i class="bi bi-eye me-1"></i>View</button>`,
    },
  ],
  pageLength: 25,
  layout: {
    topEnd: "",
    bottomEnd: "",
    bottomStart: "info",
  },
} as Config;
</script>

<template>
  <PanelGrid :columns="1" class="partner-activity-grid">
    <PanelCard>
      <PageHeader
        title="Partner Activity"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Partners', href: '/partners' },
          { label: 'Activity' }
        ]"
      >
        <template #actions>
          <router-link
            to="/partners/issues"
            class="bb-btn-secondary-cta d-none d-md-inline"
          >
            <i class="bi bi-exclamation-triangle me-2"></i>Issues
          </router-link>
          <router-link to="/partners" class="bb-btn-secondary-cta d-none d-md-inline">
            <i class="bi bi-arrow-left me-2"></i>Back to Overview
          </router-link>
        </template>
      </PageHeader>

      <div class="card mb-4 mt-4">
        <div class="card-body">
          <h2 class="card-title">Filters</h2>
          <div class="row g-3">
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Search</label>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control"
                placeholder="Search activities..."
              />
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Client</label>
              <select v-model="selectedClient" class="form-select">
                <option value="">All Clients</option>
                <option v-for="client in uniqueClients" :key="client.id" :value="client.id">
                  {{ client.name }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Category</label>
              <select v-model="selectedCategory" class="form-select">
                <option value="">All Categories</option>
                <option v-for="cat in ACTIVITY_CATEGORY_OPTIONS" :key="cat.value" :value="cat.value">
                  {{ cat.label }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">Severity</label>
              <select v-model="selectedSeverity" class="form-select">
                <option value="">All Severities</option>
                <option v-for="sev in ACTIVITY_SEVERITY_OPTIONS" :key="sev.value" :value="sev.value">
                  {{ sev.label }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">From Date</label>
              <input v-model="dateFrom" type="date" class="form-control" />
            </div>
            <div class="col-12 col-md-6 col-lg-3">
              <label class="form-label">To Date</label>
              <input v-model="dateTo" type="date" class="form-control" />
            </div>
            <div class="col-12 col-md-6 col-lg-3 d-flex align-items-end">
              <button @click="clearFilters" class="bb-btn-secondary-cta w-100">
                <i class="bi bi-x-circle me-1"></i>Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <DataView
        v-else
        :data="filteredActivities"
        :view-mode="'table'"
        :options="tableOptions"
        @row-click="(activity: PartnerActivityRow) => orgSwitch.open(activity)"
      >
        <!-- Mobile card — mirrors the dashboard overview activity item (PartnerActivity.vue):
             icon + title, a meta line with client/category, severity badge and relative time. -->
        <template #grid-card="{ row: activity }">
          <div class="card h-100 card-hover">
            <div class="card-body">
              <div class="d-flex align-items-start gap-3">
                <i
                  :class="[activityIconClass(activity), activityColorClass(activity.severity)]"
                  class="fs-5 mt-1 flex-shrink-0"
                ></i>
                <div class="flex-grow-1 min-w-0">
                  <div class="d-flex justify-content-between align-items-start gap-2 mb-1">
                    <h4 class="mb-0 fw-medium">{{ renderActivityTitle(activity) }}</h4>
                    <span class="badge flex-shrink-0" :class="activitySeverityBadgeClass(activity.severity)">
                      {{ activity.severity }}
                    </span>
                  </div>
                  <div class="d-flex flex-wrap align-items-center gap-2 small">
                    <span class="badge bg-secondary">{{ activity.clientOrgName }}</span>
                    <span class="text-muted">{{ activity.category }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="card-footer bg-transparent border-top-0">
              <small class="text-muted"><i class="bi bi-clock me-1"></i>{{ formatActivityTimestamp(activity.occurredOn) }}</small>
            </div>
          </div>
        </template>

        <template #empty-state>
          <div class="text-center py-5">
            <i class="bi bi-activity display-1 text-muted"></i>
            <h3 class="mt-3 text-muted">No activity found</h3>
            <p class="text-muted">Try adjusting your filters to see results.</p>
          </div>
        </template>
      </DataView>
    </PanelCard>
  </PanelGrid>

  <OrganizationSwitchModal
    v-bind="orgSwitch.modalProps.value"
    @update:show="orgSwitch.onShowChange"
    @cancelled="orgSwitch.onCancelled"
  />
</template>
